import {
  idDocumentState,
  IdDocumentStateType,
} from 'services/identityDocument/indentityDocument.config';
import { UserProfile } from './user.types';

/**
 * Compute days between now, according to the server side timestamp, and the
 * expiration date.
 * If the output is lower than `0`, it means that the document has expired.
 * @param expirationDate a dd/mm/yyyy date.
 * @param verifiedTimestamp
 */
export function computeDaysBeforeExpiration(
  expirationDate: string,
  verifiedAt: string,
): number {
  // Date parses according to en-US locale, hence wants 'mm/gg/yyyy',
  const parsedExpirationDate = new Date(
    expirationDate.replace(/^(\d+)\/(\d+)\/(\d+)$/g, '$2/$1/$3'),
  );

  if (Number.isNaN(parsedExpirationDate.getTime())) {
    return -1;
  }

  const amountOfDaysBetweenExpirationDateAndNow = Math.ceil(
    (parsedExpirationDate.getTime() - new Date(verifiedAt).getTime()) /
      86_400_000,
  );

  return amountOfDaysBetweenExpirationDateAndNow;
}

/**
 * Computes the state of the user document received from the api.
 * @param userProfile User data.
 * @param verifiedAt Lastest server timestamp.
 * @see idDocumentState in 'services/identityDocument/indentityDocument.config'.
 */
export function computeIdDocumentState(
  userProfile: UserProfile,
  verifiedAt: string,
): IdDocumentStateType {
  const documentState = userProfile.document.documentState;
  const expirationDate = userProfile.document.expirationDate;

  if (documentState === idDocumentState.serviceNotAvaiable) {
    return idDocumentState.serviceNotAvaiable;
  }

  if (documentState === idDocumentState.notSent) {
    return idDocumentState.notSent;
  }

  if (documentState === idDocumentState.received) {
    return idDocumentState.received;
  }

  if (documentState === idDocumentState.pendingApproval) {
    return idDocumentState.pendingApproval;
  }

  if (documentState === idDocumentState.rejected) {
    return idDocumentState.rejected;
  }

  const amountOfDaysBetweenExpirationDateAndNow = computeDaysBeforeExpiration(
    expirationDate,
    verifiedAt,
  );

  if (amountOfDaysBetweenExpirationDateAndNow < 0) {
    return idDocumentState.expired;
  }
  if (amountOfDaysBetweenExpirationDateAndNow < 31) {
    return idDocumentState.expiringSoon;
  }

  return documentState;
}
